import type { TaskItem } from '@/api/task/types';
import { defineStore } from 'pinia';
import { taskApi } from '@/api';

export const useTaskStore = defineStore('task', () => {
  // 任务列表
  const taskList = ref<TaskItem[]>([]);

  // 加载状态
  const loading = ref(false);

  // 当前任务
  const currentTask = ref<TaskItem | null>(null);

  // 获取任务列表
  const fetchTasks = async (params?: {
    page?: number;
    pageSize?: number;
    status?: string;
  }) => {
    try {
      loading.value = true;
      const data = await taskApi.getTaskList(params);
      if (data && data.success) {
        taskList.value = data.data.list;
        return data.data;
      }
    }
    catch (error) {
      console.error('获取任务列表失败:', error);
    }
    finally {
      loading.value = false;
    }
    return null;
  };

  // 根据ID获取任务详情
  const fetchTaskById = async (id: string) => {
    try {
      const data = await taskApi.getTaskById(id);
      if (data && (data as any).success)
        return (data as any).data;
      if (data && (data as any).code === 200)
        return (data as any).data;
    }
    catch (error) {
      console.error('获取任务详情失败:', error);
    }
    return null;
  };

  // 创建任务
  const createTask = async (taskData: any) => {
    try {
      const response = await taskApi.createTask(taskData);
      if (response && response.success) {
        // 重新获取任务列表
        await fetchTasks();
        return response.data;
      }
    }
    catch (error) {
      console.error('创建任务失败:', error);
    }
    return null;
  };

  // 更新任务
  const updateTask = async (id: string, taskData: any) => {
    try {
      const response = await taskApi.updateTask(id, taskData);
      if (response && response.success) {
        // 更新本地任务列表
        const index = taskList.value.findIndex(task => task.id === id);
        if (index > -1) {
          taskList.value[index] = { ...taskList.value[index], ...response.data };
        }
        // 更新当前任务
        if (currentTask.value?.id === id) {
          currentTask.value = { ...currentTask.value, ...response.data };
        }
        return response.data;
      }
    }
    catch (error) {
      console.error('更新任务失败:', error);
    }
    return null;
  };

  // 根据ID获取任务
  const getTaskById = (id: string) => {
    if (!taskList.value) {
      return null;
    }
    return taskList.value.find(task => task.id === id) || null;
  };

  // 设置当前任务
  const setCurrentTask = (task: TaskItem) => {
    currentTask.value = task;
  };

  // 获取当前任务
  const getCurrentTask = () => {
    return currentTask.value;
  };

  return {
    taskList,
    loading,
    currentTask,
    fetchTasks,
    fetchTaskById,
    createTask,
    updateTask,
    getTaskById,
    setCurrentTask,
    getCurrentTask,
  };
}, {
  persist: true, // Enable persistence for the task store
});
